import {inject, Injectable} from '@angular/core';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {TeamMembersStateModel} from './state.model';
import {
  LoadTeamMembers,
  LoadTeamMembersFailure,
  LoadTeamMembersSuccess,
  SetTeamMembersPage,
  SetTeamMembersPageSize
} from './actions';
import {TeamMembersService} from '../team-members.service';
import {catchError, tap} from 'rxjs/operators';

@State<TeamMembersStateModel>({
  name: 'teamMembers',
  defaults: {
    teamMembers: [],
    isLoading: false,
    error: undefined,
    page: 1,
    pageSize: 10,
    total: 0
  }
})
@Injectable()
export class TeamMembersState {
  private teamMembersService = inject(TeamMembersService);

  @Selector()
  static teamMembers(state: TeamMembersStateModel) {
    return state.teamMembers;
  }

  @Selector()
  static isLoading(state: TeamMembersStateModel) {
    return state.isLoading;
  }

  @Selector()
  static error(state: TeamMembersStateModel) {
    return state.error;
  }

  @Selector()
  static pagination(state: TeamMembersStateModel) {
    return {
      page: state.page + 1,
      pageSize: state.pageSize,
      total: state.total
    };
  }

  @Action(LoadTeamMembers)
  loadTeamMembers({ patchState, dispatch }: StateContext<TeamMembersStateModel>, { page, pageSize }: LoadTeamMembers) {
    patchState({ isLoading: true, error: undefined });

    return this.teamMembersService.getTeamMembers(page, pageSize).pipe(
      tap(response => {
        if (response.error) {
          const error = response.error instanceof Error
            ? response.error
            : new Error(typeof response.error === 'string' ? response.error : 'Unknown error');
          dispatch(new LoadTeamMembersFailure(error));
        } else if (response.data) {
          // Use the total count from the API response
          const total = response.total;
          dispatch(new LoadTeamMembersSuccess(response.data, total));
        }
      }),
      catchError(error => {
        dispatch(new LoadTeamMembersFailure(error));
        return [];
      })
    );
  }

  @Action(LoadTeamMembersSuccess)
  loadTeamMembersSuccess({ patchState }: StateContext<TeamMembersStateModel>, { teamMembers, total }: LoadTeamMembersSuccess) {
    patchState({
      teamMembers,
      total,
      isLoading: false
    });
  }

  @Action(LoadTeamMembersFailure)
  loadTeamMembersFailure({ patchState }: StateContext<TeamMembersStateModel>, { error }: LoadTeamMembersFailure) {
    patchState({
      error,
      isLoading: false
    });
  }

  @Action(SetTeamMembersPage)
  setPage({ patchState, dispatch, getState }: StateContext<TeamMembersStateModel>, { page }: SetTeamMembersPage) {
    patchState({ page });
    const { pageSize } = getState();
    dispatch(new LoadTeamMembers(page, pageSize));
  }

  @Action(SetTeamMembersPageSize)
  setPageSize({ patchState, dispatch, getState }: StateContext<TeamMembersStateModel>, { pageSize }: SetTeamMembersPageSize) {
    patchState({ pageSize });
    const { page } = getState();
    dispatch(new LoadTeamMembers(page, pageSize));
  }
}
